import {memoize, shallowMemoize} from '@sail/utils';
import addCarryOverIdentifiers from 'src/internal/addCarryOverIdentifiers';
import tokenizePath from 'src/internal/compiler/tokenizePath';
import replaceGlobal from 'src/internal/replaceGlobal';
import stripTrailingSlash from 'src/internal/stripTrailingSlash';

import type {RouteNames} from 'src/types';

// Helper regular expressions to build the final one.
const rParam = /:[^:/(){}]+(?:\([^()]+\))?[?!]?/;
const rStatic = /[^/{}]+/;
const rPart = new RegExp(`/(?:${rParam.source}|${rStatic.source})`);
const rFull = new RegExp(
  `\\{(?:${rPart.source})+\\}[?!]?|${rPart.source}`,
  'g',
);

const parsePath = memoize((path: string) => {
  return replaceGlobal(stripTrailingSlash(path));
});

export type RouteMatchTree = {
  /** The node property. Represents the route names associated with this node. */
  n?: Array<RouteNames>;
  /** The static property. A map of static path segments to their corresponding subtrees. */
  s?: Record<string, RouteMatchTree>;
  /** The params property. A map of dynamic (parameter) path segments to their corresponding subtrees. */
  p?: Record<string, RouteMatchTree>;
};

type TokenizedRoutes = {
  [K in RouteNames]?: ReturnType<typeof tokenizePath>;
};

type RootRouteMatchTree = RouteMatchTree & {
  /** The order property. A map of route names to their order in the tree. */
  l: Array<RouteNames>;
  /** The path of the route tokenized **/
  t: TokenizedRoutes;
};

function buildTree(
  result: RouteMatchTree,
  pieces: RegExpMatchArray | null,
  name: string,
): Array<RouteMatchTree> {
  const leafNodes: Array<RouteMatchTree> = [];

  function traverse(index: number, currentTree: RouteMatchTree): void {
    if (!pieces || index >= pieces.length) {
      leafNodes.push(currentTree);
      return;
    }

    const piece = pieces[index];
    const isOptional = piece.endsWith('?') || piece.endsWith('!');
    const key = isOptional ? piece.slice(0, -1) : piece;
    const isGroup = key.startsWith('{') && key.endsWith('}');

    // If an item is a group we treat the group as a smaller pattern, thus
    // calling build tree. Then, over each leaf node, we deep traverse the
    // children. Otherwise, we just take the parameter and add it to the current
    // tree.
    if (isGroup) {
      const innerPieces = key.slice(1, -1).match(rFull);
      const innerLeafNodes = buildTree(currentTree, innerPieces, name);

      innerLeafNodes.forEach((leafNode) => {
        traverse(index + 1, leafNode);
      });
    } else {
      const fragment = key.substr(1);
      const slot: 'p' | 's' = fragment.startsWith(':') ? 'p' : 's'; // "param" or "static".
      let id = fragment.startsWith(':')
        ? fragment.slice(1)
        : fragment.toLowerCase();

      // adding the RouteName to the route to avoid collisions on optional properties
      if (slot === 'p' && isOptional) {
        id = `${name}${id}`;
      }

      currentTree[slot] = currentTree[slot] || {};
      // TODO: using a variable just to make TS happy, this can be removed once we hit TS 5.5. See https://devblogs.microsoft.com/typescript/announcing-typescript-5-5/#control-flow-narrowing-for-constant-indexed-accesses2
      const currentSlot = currentTree[slot];
      if (currentSlot) {
        currentSlot[id] = currentSlot[id] || {};
        traverse(index + 1, currentSlot[id]);
      }
    }

    // If data was optional we also traverse the next item onto the current
    // item, effectively adding a branch skipping the extra item.
    if (isOptional) {
      traverse(index + 1, currentTree);
    }
  }

  traverse(0, result);

  return leafNodes;
}

export default shallowMemoize(function toTree(
  routes: Record<
    RouteNames,
    string | readonly [string, (x: unknown) => Record<string, unknown>]
  >,
): RootRouteMatchTree {
  const tree: RootRouteMatchTree = {
    l: Object.keys(routes) as Array<RouteNames>,
    t: {},
  };

  for (const _name in routes) {
    if (routes.hasOwnProperty(_name)) {
      const name = _name as RouteNames;
      const route = routes[name];
      const pattern =
        typeof route === 'string' ? parsePath(route) : parsePath(route[0]);
      const pieces = pattern.match(rFull);

      tree.t[name as RouteNames] = tokenizePath(
        addCarryOverIdentifiers(pattern),
      );

      if (pieces) {
        const leafNodes = buildTree(tree, pieces, name);

        leafNodes.forEach((leafNode) => {
          if (!leafNode.n) {
            leafNode.n = [name];
          } else {
            leafNode.n.push(name);
          }
        });
      }
      // We assume it's the ROOT scenario
      else {
        tree.n = [name];
      }
    }
  }

  return tree;
});
